import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import DisplayPrice from "./DisplayPrice";
import {
  getPlanLabelKey,
  Subscription,
} from "../services/subscription/subscriptionModel";
import { cx } from "../react-helpers/css";
import { parseISO } from "date-fns";

// Same 4 lines as DisplayPlan.tsx: copied rather than exported from it, because a component
// file that also exports a constant breaks fast refresh (react-refresh/only-export-components).
const PLAN_FEATURES_KEYS = ["1", "2", "3", "4"] as const;

const DisplaySubscription = ({
  subscription,
  cta,
}: {
  subscription: Subscription;
  cta?: ReactNode;
}) => {
  const { t } = useTranslation(["subscription", "common"]);
  const isMonthly = subscription.recurringInterval === "monthly";
  const planLabelKey = getPlanLabelKey(subscription.plan.type);

  const isTrialing = subscription.stripeSubscription?.status === "trialing";
  const isCanceled =
    subscription.stripeSubscription?.status === "canceled" ||
    !!subscription.stripeSubscription?.cancelAt;

  return (
    <div className="admin-sub">
      <div className="admin-sub_head">
        <span className="admin-sub_plan">
          {t(`subscription:label.${planLabelKey}`)}
        </span>
        {(isTrialing || isCanceled) && (
          <span className={cx(["ui-chip", isCanceled ? "--err" : "--ok"])}>
            {isCanceled
              ? t("subscription:CANCELED")
              : isTrialing && t("subscription:TRIALING")}
          </span>
        )}
      </div>
      <p className="ui-muted">{t(`subscription:subtitle.${planLabelKey}`)}</p>

      <div className="admin-sub_price">
        <DisplayPrice
          amount={
            isMonthly
              ? subscription.plan.stripeMonthlyPrice?.amount ?? 0
              : subscription.plan.stripeAnnualPrice?.amount ?? 0
          }
          currency={
            isMonthly
              ? subscription.plan.stripeMonthlyPrice?.currency
              : subscription.plan.stripeAnnualPrice?.currency
          }
          addon={
            isMonthly ? t("subscription:PER_MONTH") : t("subscription:PER_YEAR")
          }
        />
      </div>

      <ul className="admin-features">
        {PLAN_FEATURES_KEYS.map((featureKey) => (
          <li key={featureKey}>
            <span className="admin-ic --check" aria-hidden="true" />
            {t(`subscription:features.${planLabelKey}.${featureKey}`)}
          </li>
        ))}
      </ul>

      <p className="ui-muted ui-small">
        {t("subscription:END_OF_COMMITMENT")}{" "}
        <strong>
          {t("common:COMPACT_DATE", {
            replace: {
              date: parseISO(subscription.endDate),
            },
          })}
        </strong>
      </p>

      {cta}
    </div>
  );
};

export default DisplaySubscription;
